import { Alert, Box, Button, Typography } from '@mui/material';
import { useRouter } from '@/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import ThreeDRotationIcon from '@mui/icons-material/ThreeDRotation';
import { ZoneListSwticher } from './KanbanViewer/ZoneListSwticher';
import { KanbanViewer } from './KanbanViewer/KanbanViewer';
import {
  useZoneAndHistoriesForZoneManagementQuery,
  useZonesForStructureCsvQuery,
  ZoneAndHistory,
  useGetStructureNameQuery,
} from '@/__generated__/graphql';
import * as state from '@/state';
import { Snackbar } from '../shared/Snackbar';
import { PageLoader } from '../shared/PageLoader';
import { Link } from 'react-router-dom';
import { ZonesListViewer } from './ZonesListViewer';
const mainContainerStyle = {
  display: 'flex',
  alignItems: 'center',
};

const linkButtonStyles = { display: 'flex', gap: 1 };

const buttonContainerStyle = {
  ml: 'auto',
  display: 'flex',
};

export const ZonesManagement = () => {
  const router = useRouter();

  const setSelectedZone = useSetAtom(state.selectedZone);
  const [snackbarMessage, setSnackbarMessage] = useAtom(state.snackbarMessage);

  const { shouldShow, content } = snackbarMessage;
  useEffect(() => {
    setSelectedZone(null);
  }, [setSelectedZone]);

  const { inspection: structureId } = router.query;
  const getRecoilStructureName = useAtomValue(
    state.structureNameByStructureId
  );

 
  const recoilStructureName = getRecoilStructureName(
    typeof structureId === 'string' ? structureId : undefined
  );
  
  const selectedStructureId = useAtomValue(state.selectedStructureId);

  const { data: structureName } = useGetStructureNameQuery({
    variables: {
      structureId: (structureId as string) || selectedStructureId || '',
    },
    fetchPolicy: 'cache-first',
  });

  const { data, loading, error } = useZoneAndHistoriesForZoneManagementQuery({
    variables: {
      structureId: structureId as string,
    },
  });

  const { refetch } = useZonesForStructureCsvQuery({
    variables: {
      structureId: structureId as string,
    },
    skip: true,
  });

  const closeSnack = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarMessage({ shouldShow: false, content: undefined });
  };

  const downloadCsvReport = useCallback(
    (rawCsvString: string) => {
      const CSV_FILE_TYPE = 'text/csv;charset=utf-8';

      const byteOrderMark = '\uFEFF';

      const csvBlob = new Blob([byteOrderMark, rawCsvString], {
        type: CSV_FILE_TYPE,
      });

      const name = structureName?.structure?.name;

      // format 24-Aug-2023-08-43:AM
      const currentDateTime = new Date().toLocaleString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });

      const fileName = `${name || 'structure'}-${currentDateTime}-report.csv`;

      const link = document.createElement('a');
      link.href = URL.createObjectURL(csvBlob);
      link.target = '_blank';
      link.download = fileName;
      link.style.display = 'none';
      document.body.append(link);
      link.click();
      link.remove();
    },
    [structureName?.structure?.name]
  );

  const handleDownloadReport = useCallback(() => {
    refetch()
      .then((response) => {
        downloadCsvReport(response.data.zonesForStructureCSV);
      })
      .catch(() => {
        setSnackbarMessage({
          shouldShow: true,
          content: (
            <Alert severity="error">Could not download report. Please try again later</Alert>
          ),
        });
      });
  }, [downloadCsvReport, refetch, setSnackbarMessage]);

  if (loading) {
    return (
      <Box sx={{ position: 'relative', top: '25rem' }}>
        <PageLoader />
      </Box>
    );
  }

  if (error && !data) {
    return <>{`KanbanViewer component: ${error.message}`}</>;
  }

  const zones = data?.zonesAndHistoriesFor;

  if (!zones) {
    return <>No zones found</>;
  }

  const tabsData = [
    {
      label: 'By Status',
      content: <KanbanViewer zones={zones as ZoneAndHistory[]} />,
    },
    {
      label: 'All Tasks',
      content: <ZonesListViewer zones={zones as ZoneAndHistory[]} />
    },
  ];

  return (
    <>
      <Box sx={mainContainerStyle}>
        <Typography variant="h5" fontWeight={800}>
          {structureName?.structure?.name || recoilStructureName}
        </Typography>
        <Box sx={buttonContainerStyle}>
          <Link
            to={{
              pathname: `/inspection/${structureId}`,
            }}
          >
            <Button sx={linkButtonStyles} color="info">
              <ThreeDRotationIcon />
              View 3D
            </Button>
          </Link>
          <Button color="info" sx={linkButtonStyles} onClick={handleDownloadReport}>
            <SaveAltIcon />
            Download Report
          </Button>
        </Box>
      </Box>
      <ZoneListSwticher data={tabsData} />
      <Snackbar open={shouldShow} onClose={closeSnack}>
        {content}
      </Snackbar>
    </>
  );
};
